import type {
  ActForOffer,
  ActForPerson,
  ActingFor,
  ApprovalDecision,
  ApprovalDetail,
  ApprovalKind,
  Me,
  ShareFraction,
} from '@myrewards/contracts';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useState, type ReactElement, type ReactNode } from 'react';
import { meQuery } from '../../api';
import { actingForKeys, waitingOnOthersQuery } from '../../api/actingFor';
import { approvalKeys, decide, movePerson, saveReview } from '../../api/ops';
import { ActForStep, type ActForDone } from '../../acting-for/ActForStep';
import {
  actForProblemWords,
  firstName,
  recordedLine,
  refusalRefreshes,
  saySoFromTheNotice,
  StepProblem,
  withNoteAndFiles,
} from '../../acting-for/words';
import { Label, RadioButton, RadioField, RadioGroup } from 'react-aria-components';
import { formatMoney, plural, TextField, useToast } from '../../ui';
import af from '../../acting-for/ActingFor.module.css';
import { amountWords, TYPE_LABEL } from '../labels';
import type { ActedDone } from './ActedConfirmation';
import { includedWords } from './parts';
import {
  afterTopUp,
  declineNoteHint,
  demandOf,
  editedTotals,
  isDirty,
  overBudgetPools,
  paidFromLines,
  reviewPayload,
  sameShare,
  splitWords,
  taaPools,
  topUpPayload,
  type Edits,
  type TopUpDraft,
} from './presenter';
import { refreshAfterApproval } from './useApprovalCommand';

// APP-148: a Stock Admin doing an Executive's or a Sector Lead's work for them, on the approval
// pages. Every action here goes through the step (`ActForStep`): who it's for, files and a note,
// what happens, how it's recorded. The overage's approve and decline (Task 9a); a PAA's or TAA's
// decision and its changes, and recording an Executive Discretionary decision (Task 9b). The
// Sector Lead's inline card is `LeadDecision.tsx`.

/**
 * Why the step was refused, on which award and which of its pages: the page that replaces it says
 * it, and only for that award.
 */
export interface ActingRefusal {
  awardId: string;
  kind: ApprovalKind | null;
  words: string;
}

/** A refusal the page holds: when it came, and whether the page then moved on under the step. */
export interface HeldRefusal extends ActingRefusal {
  /** When the award was last read as it came (ms): only a later read says if the page moved on. */
  at: number;
  movedOn: boolean;
}

/**
 * What becomes of a held refusal as the page reads its award again (R57). It belongs to this
 * visit to this award: leaving the award drops it. The first read after it says whether the page
 * moved on under the step (someone decided first): then the page says why nothing was saved. If
 * it didn't, the words were the step's, and a later change (someone else deciding it) never
 * brings them back.
 */
export function settleRefusal(
  held: HeldRefusal,
  detail: ApprovalDetail,
  readAt: number,
): HeldRefusal | null {
  if (held.awardId !== detail.award.id) return null;
  if (held.movedOn || readAt <= held.at) return held;
  return held.kind === detail.kind ? null : { ...held, movedOn: true };
}

/** Whether a top-up adds anything: cash, or options when the pool takes them. */
export function hasTopUp(draft: TopUpDraft): boolean {
  return (draft.cashCents ?? 0) > 0 || (draft.withOptions && (draft.options ?? 0) > 0);
}

/** "Approve with a $2,913 top-up", "Approve with top-ups to Terrestrial and …", "Approve". */
export function approveWhat(drafts: readonly TopUpDraft[]): string {
  const added = drafts.filter(hasTopUp);
  const [only] = added;
  if (only === undefined) return 'Approve';
  if (added.length === 1)
    return `Approve with a ${amountWords(only.cashCents ?? 0, only.withOptions ? (only.options ?? 0) : 0)} top-up`;
  return `Approve with top-ups to ${added.map((d) => d.pool.name).join(' and ')}`;
}

/** "is at $0", "has $1,200 left", "stays $500 over budget": a pool after its top-up. */
export function afterWords(draft: TopUpDraft): string {
  const after = afterTopUp(draft);
  if (after.cashCents === 0) return 'is at $0';
  return after.cashCents > 0
    ? `has ${formatMoney(after.cashCents)} left`
    : `stays ${formatMoney(-after.cashCents)} over budget`;
}

/** "the PAA/TAA pool", "the Maritime HydroVision pool": a pool's name, once with "pool". */
function thePool(name: string): string {
  return `the ${name.replace(/\s+pool$/i, '')} pool`;
}

/**
 * "Paid from the PAA/TAA pool ($12,000, 4 people) and the Maritime HydroVision pool ($9,000, 3
 * people). Both stay within budget, so there's no top-up." (SameAsk board).
 */
export function paidFromSentence(detail: ApprovalDetail, edits: Edits = {}): string {
  const rows = paidFromLines(detail, edits);
  const parts = rows.map(
    (r) =>
      `${thePool(r.pool.name)} (${amountWords(r.cashCents, detail.award.type === 'TAA' ? 0 : r.options)}, ${plural(r.people, 'person', 'people')})`,
  );
  const within =
    rows.length > 1
      ? 'Both stay within budget, so there’s no top-up.'
      : 'It stays within budget, so there’s no top-up.';
  return `Paid from ${parts.join(' and ')}. ${within}`;
}

/** The top-up lines of a step that approves over budget (ForExec board). */
function topUpLines(
  drafts: readonly TopUpDraft[],
  id: string,
  restOf: string,
  who: string | null,
): ReactNode {
  return drafts.map((d) => (
    <li key={d.pool.id}>
      {hasTopUp(d)
        ? `${d.pool.name} gets a ${amountWords(d.cashCents ?? 0, d.withOptions ? (d.options ?? 0) : 0)} top-up and ${afterWords(d)} for ${restOf}. The ledger entry reads “Over budget: ${id}”, by you for ${who ?? 'the Executive you name'}.`
        : `${d.pool.name} gets no top-up and ${afterWords(d)} for ${restOf}.`}
    </li>
  ));
}

/** "the rest of 2026", or "the rest of the year" when the award's year isn't known. */
function restOfYear(detail: ApprovalDetail): string {
  const year = detail.award.fiscalYear ?? null;
  return year === null ? 'the rest of the year' : `the rest of ${String(year)}`;
}

/** The first name of whoever the offer names, by id ("Elena"). */
function firstNameIn(offer: ActForOffer): (personId: string) => string {
  return (personId) => firstName(offer.people.find((p) => p.id === personId)?.name ?? '');
}

/** "Elena gets a notice in the bell and by email, with your note." */
function noticeLine(who: string | null, added: { note: boolean; files: number }): string {
  return `${who ?? 'The Executive you name'} gets a notice in the bell and by email${withNoteAndFiles(added.note, added.files)}.`;
}

function optional(text: string): string | undefined {
  const trimmed = text.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * An action done for someone (APP-148): `send` carries `actingFor`; the saved answer refreshes
 * the page as every approval command does (`refreshAfterApproval`). A refusal means the page's
 * facts moved on (the switch went off, the person named left the role, someone decided first),
 * so they're read again: the "for an Executive" buttons and the people offered follow (review
 * focus 1 and 3), and the page hears the words, to say them if it moves on under the step.
 */
export function useActing(detail: ApprovalDetail, onRefused?: (refusal: ActingRefusal) => void) {
  const queryClient = useQueryClient();
  const id = detail.award.id;
  return async (
    send: () => Promise<ApprovalDetail>,
    then: (next: ApprovalDetail) => void,
  ): Promise<void> => {
    let next: ApprovalDetail;
    try {
      next = await send();
    } catch (error) {
      if (refusalRefreshes(error)) {
        onRefused?.({ awardId: id, kind: detail.kind, words: actForProblemWords(error) });
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: approvalKeys.one(id) }),
          queryClient.invalidateQueries({ queryKey: approvalKeys.list }),
          queryClient.invalidateQueries({ queryKey: waitingOnOthersQuery.queryKey }),
          queryClient.invalidateQueries({ queryKey: actingForKeys.people }),
        ]);
      }
      throw error;
    }
    then(next);
    void refreshAfterApproval(queryClient, id, next);
  };
}

/** A decision done for someone: `useActing` with the decision route. */
export function useActingDecision(
  detail: ApprovalDetail,
  onRefused?: (refusal: ActingRefusal) => void,
) {
  const act = useActing(detail, onRefused);
  return (decision: ApprovalDecision, then: (next: ApprovalDetail) => void) =>
    act(() => decide(detail.award.id, decision), then);
}

/**
 * When the server recorded what was done for someone (R125), from its answer: the record it kept,
 * else an approval's own time. Never the browser's clock; null when an answer says neither.
 */
export function recordedAt(next: ApprovalDetail): string | null {
  return next.actedFor?.at ?? next.decision.approvedAt;
}

/** What every confirmation starts from: the award, who it was for, the note and files. */
function actedBase(
  detail: ApprovalDetail,
  done: ActForDone,
): Pick<ActedDone, 'awardId' | 'person' | 'note' | 'files'> {
  return { awardId: detail.award.id, person: done.person, note: done.note, files: done.files };
}

/** An approval's top-ups and the first pool after them, as the confirmation shows them. */
function approvedTopUps(
  drafts: readonly TopUpDraft[],
): Pick<ActedDone, 'topUps' | 'pool' | 'goesTo' | 'toCreator'> {
  const first = drafts[0];
  return {
    topUps: drafts.filter(hasTopUp).map((d) => ({
      pool: d.pool.name,
      cashCents: d.cashCents ?? 0,
      options: d.withOptions ? (d.options ?? 0) : 0,
      afterCents: afterTopUp(d).cashCents,
    })),
    pool:
      first === undefined
        ? null
        : { name: first.pool.name, leftCents: afterTopUp(first).cashCents },
    goesTo: 'stock_admin',
    toCreator: null,
  };
}

// --- Over budget (Overage, ForExec boards) ---------------------------------------------------------

/** Which overage step is open, with the offer it opened with. */
export interface OverageStepOpen {
  kind: 'approve' | 'decline';
  offer: ActForOffer;
}

/**
 * Approve with the top-up, or decline, for an Executive (Overage, ForExec boards). The step keeps
 * the offer it opened with, so a switch turned off meanwhile still shows why it was refused.
 */
export function OverageStep({
  detail,
  drafts,
  step,
  me,
  onActed,
  onRefused,
  onClose,
}: {
  detail: ApprovalDetail;
  drafts: readonly TopUpDraft[];
  step: OverageStepOpen;
  me: Me;
  onActed: (done: ActedDone) => void;
  onRefused?: (refusal: ActingRefusal) => void;
  onClose: () => void;
}): ReactElement {
  const { award } = detail;
  const creator = award.createdBy.name;
  const id = award.displayId ?? 'This award';
  const offer = detail.actingFor ?? step.offer;
  const act = useActingDecision(detail, onRefused);
  const [declineNote, setDeclineNote] = useState('');
  const approving = step.kind === 'approve';
  const actor = me.person.displayName;
  const nameOf = firstNameIn(offer);
  const firstPool = drafts[0];

  const confirm = async (actingFor: ActingFor, done: ActForDone): Promise<void> => {
    const base = { ...actedBase(detail, done), role: 'executive' as const, goesTo: null };
    if (approving) {
      const payload = topUpPayload(drafts);
      if (!payload.ok) throw new StepProblem(payload.message);
      await act(
        { action: 'approve', version: award.version, topUps: payload.topUps, actingFor },
        (next) => {
          onActed({
            ...base,
            kind: 'approve',
            at: recordedAt(next),
            ...approvedTopUps(drafts),
          });
        },
      );
      return;
    }
    const reason = optional(declineNote);
    await act(
      {
        action: 'decline',
        version: award.version,
        actingFor,
        ...(reason !== undefined && { reason }),
      },
      (next) => {
        onActed({
          ...base,
          kind: 'decline',
          at: recordedAt(next),
          topUps: [],
          pool:
            firstPool === undefined
              ? null
              : {
                  name: firstPool.pool.name,
                  leftCents:
                    firstPool.remainingAfter.cashCents +
                    demandOf(detail, firstPool.pool.id).cashCents,
                },
          toCreator: reason ?? null,
        });
      },
    );
  };

  const summary = (personId: string | null, added: { note: boolean; files: number }): ReactNode => {
    const who = personId === null ? null : nameOf(personId);
    const told = noticeLine(who, added);
    if (!approving)
      return (
        <>
          <li>
            {id} goes back to {creator} to reduce. No budget changes.
          </li>
          <li>
            {told} {creator} hears it was declined, and the other Executives see that it’s done, as
            they do today.
          </li>
        </>
      );
    return (
      <>
        <li>{id} is approved and goes to the Stock Admin team.</li>
        {topUpLines(drafts, id, restOfYear(detail), who)}
        <li>{told} The other Executives see that it’s done, as they do today.</li>
      </>
    );
  };

  return (
    <ActForStep
      title={approving ? 'Approve for an Executive' : 'Decline for an Executive'}
      subtitle={
        approving
          ? `${id} · ${approveWhat(drafts)}`
          : `${id} · Decline · back to ${firstName(creator)} to reduce`
      }
      actorName={actor}
      offer={offer}
      firstNameOf={nameOf}
      {...(!approving && {
        whoHint: 'Any one Executive can decline this. Naming one records who it was for.',
      })}
      summaryTitle={approving ? 'When you approve' : 'When you decline'}
      summary={summary}
      recorded={(p) => recordedLine(approving ? 'Approved' : 'Declined', actor, p, 'executive')}
      confirmLabel={(p) => `${approving ? 'Approve' : 'Decline'} for ${firstName(p.name)}`}
      // A decline sends the award back: nothing is held, and the Executive can say so.
      {...(!approving && { ifNotWanted: saySoFromTheNotice })}
      onConfirm={confirm}
      onCancel={onClose}
    >
      {!approving && (
        <TextField
          label={`Note to ${creator} (optional)`}
          // APP-204: who reads it, when one of them doesn't see the budget.
          description={`${declineNoteHint(detail) ?? `${creator} reads it with the award.`} It’s yours to them, separate from the note for the Executive.`}
          multiline
          value={declineNote}
          onChange={setDeclineNote}
        />
      )}
    </ActForStep>
  );
}

// --- A PAA or TAA, or an older Executive award (SameAsk board; Main board rows 2 and 4) ------------

/** What an approval offers a Stock Admin: who they may name, and what each may do on it. */
export type ApprovalOffer = NonNullable<ApprovalDetail['actingFor']>;

/** Which Executive decision step is open, with the offer it opened with. */
export interface ExecutiveStepOpen {
  kind: 'approve' | 'reject';
  offer: ApprovalOffer;
}

/**
 * Approve or reject a PAA or TAA (or an Executive award waiting since before APP-121) for an
 * Executive. The page's own fields go with it: the Board's decision date, the decision's note and,
 * over budget, the top-up.
 */
export function ExecutiveStep({
  detail,
  drafts,
  decision,
  step,
  onActed,
  onRefused,
  onClose,
}: {
  detail: ApprovalDetail;
  /** The top-up for each pool over budget; none within budget. */
  drafts: readonly TopUpDraft[];
  /** The page's Board decision date and note. */
  decision: { date: string; note: string };
  step: ExecutiveStepOpen;
  onActed: (done: ActedDone) => void;
  onRefused?: (refusal: ActingRefusal) => void;
  onClose: () => void;
}): ReactElement {
  const { award } = detail;
  const { data: me } = useSuspenseQuery(meQuery);
  const creator = award.createdBy.name;
  const id = award.displayId ?? 'This award';
  const offer = detail.actingFor ?? step.offer;
  const act = useActingDecision(detail, onRefused);
  const [reason, setReason] = useState('');
  const approving = step.kind === 'approve';
  const actor = me.person.displayName;
  const nameOf = firstNameIn(offer);
  const over = overBudgetPools(detail).length > 0;
  const totals = editedTotals(detail, {});
  // Changing it first is the Executive's too, through the same step (SameAsk board): a TAA's
  // split or who's on which pool; for a PAA, its amounts.
  const changes =
    offer.actions.changeTaaSplit || offer.actions.movePerson
      ? 'a different split between the pools, or to move someone'
      : offer.actions.edit
        ? 'different amounts'
        : null;

  const confirm = async (actingFor: ActingFor, done: ActForDone): Promise<void> => {
    const base = { ...actedBase(detail, done), role: 'executive' as const };
    if (approving) {
      const payload = over ? topUpPayload(drafts) : null;
      if (payload !== null && !payload.ok) throw new StepProblem(payload.message);
      const note = optional(decision.note);
      await act(
        {
          action: 'approve',
          version: award.version,
          ...(note !== undefined && { note }),
          ...(decision.date !== '' && { boardDecisionDate: decision.date }),
          ...(payload?.ok === true && { topUps: payload.topUps }),
          actingFor,
        },
        (next) => {
          onActed({
            ...base,
            kind: 'approve',
            at: recordedAt(next),
            ...approvedTopUps(drafts),
          });
        },
      );
      return;
    }
    const why = optional(reason);
    await act(
      {
        action: 'reject',
        version: award.version,
        ...(why !== undefined && { reason: why }),
        actingFor,
      },
      (next) => {
        onActed({
          ...base,
          kind: 'reject',
          at: recordedAt(next),
          topUps: [],
          pool: null,
          goesTo: null,
          toCreator: why ?? null,
        });
      },
    );
  };

  const summary = (personId: string | null, added: { note: boolean; files: number }): ReactNode => {
    const who = personId === null ? null : nameOf(personId);
    if (!approving)
      return (
        <>
          <li>
            {id} is turned down and won’t be paid. {creator} is told
            {optional(reason) === undefined ? '' : ', with your reason'}.
          </li>
          <li>
            {noticeLine(who, added)} The other Executives see that it’s done, as they do today.
          </li>
        </>
      );
    return (
      <>
        <li>
          {id} is approved and goes to the Stock Admin team.
          {award.type === 'TAA' ? ' It’s cash only: no election.' : ''}
        </li>
        {over ? (
          topUpLines(drafts, id, restOfYear(detail), who)
        ) : (
          <li>{paidFromSentence(detail)}</li>
        )}
        <li>
          {noticeLine(who, added)}
          {/* Over budget, the other Executives see it's done, as on the ForExec board. */}
          {over && ' The other Executives see that it’s done, as they do today.'}
        </li>
      </>
    );
  };

  return (
    <ActForStep
      title={approving ? 'Approve for an Executive' : 'Reject for an Executive'}
      subtitle={
        approving && over
          ? `${id} · ${approveWhat(drafts)}`
          : `${id} · ${approving ? 'Approve' : 'Reject'} · ${includedWords(totals.people, totals.of)} · ${amountWords(totals.cashCents, award.type === 'TAA' ? 0 : totals.options)}`
      }
      actorName={actor}
      offer={offer}
      firstNameOf={nameOf}
      {...(!approving && {
        whoHint: 'Any one Executive can reject this. Naming one records who it was for.',
      })}
      summaryTitle={approving ? 'When you approve' : 'When you reject'}
      summary={summary}
      recorded={(p) => recordedLine(approving ? 'Approved' : 'Rejected', actor, p, 'executive')}
      confirmLabel={(p) => `${approving ? 'Approve' : 'Reject'} for ${firstName(p.name)}`}
      {...(approving &&
        changes !== null && {
          afterSummary: (personId: string | null) => {
            const who = personId === null ? 'the Executive' : nameOf(personId);
            return `If ${who} had asked for ${changes}, cancel and change it on the page first with “Change for an Executive…”. That change goes through this same step.`;
          },
        })}
      {...(!approving && { ifNotWanted: saySoFromTheNotice })}
      onConfirm={confirm}
      onCancel={onClose}
    >
      {!approving && (
        <TextField
          label={`Reason for ${creator} (optional)`}
          description={`${creator} reads it with the award. It’s yours to them, separate from the note for the Executive.`}
          multiline
          value={reason}
          onChange={setReason}
        />
      )}
    </ActForStep>
  );
}

/** What's being changed for an Executive while they decide: amounts, a TAA's split, or who's on which pool. */
export type ChangeOpen =
  | { kind: 'amounts'; edits: Edits; offer: ApprovalOffer }
  | { kind: 'split'; share: ShareFraction; offer: ApprovalOffer }
  | { kind: 'move'; offer: ApprovalOffer };

/**
 * Change amounts, a TAA's split or who's on which pool, for an Executive while it waits on them
 * (Main board row 3, R58: "Change for an Executive…"). The Stock Admin's own move (APP-049) stays
 * beside it, as it was. A split change keeps its own reason, as the Executive's does; the step's
 * note is for the Executive. The confirm waits for a change: an amount changed, a reason for the
 * split, the person who moves.
 */
export function ChangeStep({
  detail,
  open,
  onChanged,
  onRefused,
  onClose,
}: {
  detail: ApprovalDetail;
  open: ChangeOpen;
  /** Saved: the page clears its edits or its split choice. */
  onChanged: (next: ApprovalDetail, person: ActForPerson) => void;
  onRefused?: ((refusal: ActingRefusal) => void) | undefined;
  onClose: () => void;
}): ReactElement {
  const { award } = detail;
  const { data: me } = useSuspenseQuery(meQuery);
  const toast = useToast();
  const id = award.displayId ?? 'This award';
  const offer = detail.actingFor ?? open.offer;
  const act = useActing(detail, onRefused);
  const [reason, setReason] = useState('');
  const [moving, setMoving] = useState<string | null>(null);
  const actor = me.person.displayName;
  const nameOf = firstNameIn(offer);
  const pools = taaPools(detail);
  const funding = detail.taaFunding;
  const totals = editedTotals(detail, open.kind === 'amounts' ? open.edits : {});
  const amount = `${includedWords(totals.people, totals.of)} · ${amountWords(totals.cashCents, award.type === 'TAA' ? 0 : totals.options)}`;
  // Each person on the award, moving wholly to the pool they aren't on.
  const otherPool = (poolId: string) =>
    pools === null ? null : poolId === pools.paa.id ? pools.sector : pools.paa;
  const movable = detail.lines.filter((l) => l.included && otherPool(l.poolId) !== null);
  const mover = movable.find((l) => l.id === moving) ?? null;
  const moverTo = mover === null ? null : otherPool(mover.poolId);

  const confirm = async (actingFor: ActingFor, done: ActForDone): Promise<void> => {
    if (open.kind === 'move') {
      if (mover === null || moverTo === null) throw new StepProblem('Choose who moves first.');
      await act(
        () =>
          movePerson(award.id, {
            version: award.version,
            lineId: mover.id,
            poolId: moverTo.id,
            actingFor,
          }),
        (next) => {
          toast.show({
            tone: 'done',
            title: `Moved for ${done.person.name}`,
            body: `${mover.personName} is paid from ${thePool(moverTo.name)} now. ${firstName(done.person.name)} has a notice in the bell and by email${withNoteAndFiles(done.note !== null, done.files.length)}.`,
          });
          onChanged(next, done.person);
        },
      );
      return;
    }
    const body =
      open.kind === 'amounts'
        ? { ...reviewPayload(detail, open.edits), actingFor }
        : {
            ...reviewPayload(detail, {}),
            // Back to the default clears this award's change, as the Executive's own does.
            taaShareOverride:
              funding != null && sameShare(open.share, funding.defaultShare) ? null : open.share,
            reason: reason.trim(),
            actingFor,
          };
    await act(
      () => saveReview(award.id, body),
      (next) => {
        // A split's own toast says who the app moved (TaaSplit); one toast each time.
        if (open.kind === 'amounts')
          toast.show({
            tone: 'done',
            title: `Changed for ${done.person.name}`,
            body: `${firstName(done.person.name)} has a notice in the bell and by email${withNoteAndFiles(done.note !== null, done.files.length)}.`,
          });
        onChanged(next, done.person);
      },
    );
  };

  const summary = (personId: string | null, added: { note: boolean; files: number }): ReactNode => {
    const who = personId === null ? null : nameOf(personId);
    if (open.kind === 'move')
      return (
        <>
          <li>
            {mover === null || moverTo === null
              ? 'The person you choose moves wholly to the other pool. Their charge code follows the pool.'
              : `${mover.personName} moves wholly to ${thePool(moverTo.name)}. Their charge code follows the pool.`}
          </li>
          <li>
            The move is logged with the person it touches, by you for{' '}
            {who ?? 'the Executive you name'}. {id} keeps waiting on the Executives.
          </li>
          <li>{noticeLine(who, added)}</li>
        </>
      );
    return open.kind === 'amounts' ? (
      <>
        <li>
          {id} keeps waiting on the Executives, with the new amounts: {amount}.
        </li>
        <li>
          Each change is logged with the person it touches, by you for{' '}
          {who ?? 'the Executive you name'}.
        </li>
        <li>{noticeLine(who, added)}</li>
      </>
    ) : (
      <>
        <li>
          The app moves people between {thePool(pools?.paa.name ?? 'PAA/TAA')} and{' '}
          {thePool(pools?.sector.name ?? 'sector')}, each wholly to one, to come as close to{' '}
          {splitWords(open.share)} as whole people allow. Charge codes follow each person’s pool.
        </li>
        <li>{id} keeps waiting on the Executives. Approving it is its own step.</li>
        <li>{noticeLine(who, added)}</li>
      </>
    );
  };

  return (
    <ActForStep
      title="Change for an Executive"
      subtitle={
        open.kind === 'amounts'
          ? `${id} · Change amounts · ${amount}`
          : open.kind === 'split'
            ? `${id} · Change the split to ${splitWords(open.share)} by person`
            : `${id} · Move someone between the pools`
      }
      actorName={actor}
      offer={offer}
      firstNameOf={nameOf}
      whoHint="Any one Executive can change this. Naming one records who it was for."
      summaryTitle="When you save"
      summary={summary}
      // As the record names it, a move too: "Changed by … for …" (acted-for-words RECORDED).
      recorded={(p) => recordedLine('Changed', actor, p, 'executive')}
      confirmLabel={(p) => `${open.kind === 'move' ? 'Move' : 'Change'} for ${firstName(p.name)}`}
      // Nothing to record until something changes (R64): the server records nothing for a save
      // that changes nothing, and the step never sends one.
      ready={
        open.kind === 'amounts'
          ? isDirty(detail, open.edits)
          : open.kind === 'split'
            ? reason.trim() !== ''
            : mover !== null
      }
      onConfirm={confirm}
      onCancel={onClose}
    >
      {open.kind === 'move' && (
        <RadioGroup className={af.section} value={moving} onChange={setMoving} isRequired>
          <Label className={af.fieldLabel}>
            Who moves? <span className={af.required}>Required</span>
          </Label>
          <div className={af.people}>
            {movable.map((line) => (
              <RadioField key={line.id} value={line.id}>
                <RadioButton className={af.personTile}>
                  <span className={af.personText}>
                    <span className={af.personName}>
                      {line.personName}, to {thePool(otherPool(line.poolId)?.name ?? '')}
                    </span>
                  </span>
                  <span className={af.radioMark} aria-hidden="true" />
                </RadioButton>
              </RadioField>
            ))}
          </div>
        </RadioGroup>
      )}
      {open.kind === 'split' && (
        <TextField
          label="Why change the split?"
          description="It’s kept on the award’s record with the change, as an Executive’s reason is."
          multiline
          isRequired
          value={reason}
          onChange={setReason}
        />
      )}
    </ActForStep>
  );
}

// --- Recording an Executive Discretionary decision (Main board row 6, OnAward board) ---------------

/**
 * Record an Executive Discretionary decision made outside the app, for the Executive who made it
 * (the Stock Admin's own authority; APP-148 "Unchanged authority"). The step's list is today's
 * Executives (`actingFor.people`), and the one named is the one recorded (Task 4).
 */
export function RecordStep({
  detail,
  offer,
  date,
  onActed,
  onRefused,
  onClose,
}: {
  detail: ApprovalDetail;
  offer: ActForOffer;
  /** The page's "Date approved". */
  date: string;
  onActed: (done: ActedDone) => void;
  onRefused?: (refusal: ActingRefusal) => void;
  onClose: () => void;
}): ReactElement {
  const { award } = detail;
  const { data: me } = useSuspenseQuery(meQuery);
  const id = award.displayId ?? 'This request';
  const act = useActingDecision(detail, onRefused);
  const actor = me.person.displayName;
  const live = detail.actingFor ?? offer;
  const nameOf = firstNameIn(live);
  const over = overBudgetPools(detail).length > 0;
  const budgetVisible = award.calculation.budgetVisible !== false;
  const totals = editedTotals(detail, {});
  const requester = award.createdBy;
  // The requester can be named when they're one of today's Executives (OnAward board, R66).
  const requesterOffered = live.people.some((p) => p.id === requester.id);

  const confirm = async (actingFor: ActingFor, done: ActForDone): Promise<void> => {
    await act(
      {
        action: 'record_decision',
        version: award.version,
        ...(date !== '' && { boardDecisionDate: date }),
        actingFor,
      },
      (next) => {
        onActed({
          ...actedBase(detail, done),
          role: 'executive',
          kind: 'record',
          at: recordedAt(next),
          topUps: [],
          pool: null,
          goesTo: next.award.waitingOn === 'over_budget' ? 'executives' : 'stock_admin',
          toCreator: null,
        });
      },
    );
  };

  const summary = (personId: string | null, added: { note: boolean; files: number }): ReactNode => {
    const who = personId === null ? null : nameOf(personId);
    return (
      <>
        <li>
          {!budgetVisible
            ? `${id} follows any required approvals before the Stock Admin team’s check.`
            : over
              ? `${id} goes to the Executives to approve a top-up first.`
              : `${id} goes to the Stock Admin team for the one check.`}
        </li>
        <li>
          {who === null
            ? 'The Executive you name is recorded as the one who approved it.'
            : `${who} is recorded as the Executive who approved it.`}
        </li>
        <li>{noticeLine(who, added)}</li>
      </>
    );
  };

  return (
    <ActForStep
      title={`Record for an Executive · ${id}`}
      subtitle={`${TYPE_LABEL[award.type]} requested by ${requester.name} · ${plural(totals.people, 'person', 'people')} · ${amountWords(totals.cashCents, totals.options)} · recording the Executives’ decision`}
      actorName={actor}
      offer={live}
      firstNameOf={nameOf}
      {...(requesterOffered && {
        whoHint: `${firstName(requester.name)} asked for this award and can still be named, as ${firstName(requester.name)} could approve it directly. The same goes for an Executive who is one of the people on an award.`,
      })}
      summaryTitle="When you record"
      summary={summary}
      recorded={(p) => recordedLine('Recorded', actor, p, 'executive')}
      confirmLabel={(p) => `Record for ${firstName(p.name)}`}
      unnamedLabel="Record for an Executive"
      onConfirm={confirm}
      onCancel={onClose}
    />
  );
}
